// The Inner Citadel — game data
// A deliberately different shape from the other levels: no money, no
// business plan. The resource here is attention, and the "portfolio" is a
// handful of daily practices. Four stages: The Dichotomy Sort (a real
// Stoic exercise) -> Build Your Morning System (pick a few, not everything)
// -> The Pause (provocations, played out) -> Citadel Report.
//
// This adapts Stoic philosophy for a teen/young-adult audience. Nothing
// here suggests suppressing emotion — the skill taught throughout is
// noticing the gap between a stimulus and a response, not having no
// reaction at all. Asking for help is explicitly framed as a strength.

// ---------------------------------------------------------------------------
// STAGE 1 — THE DICHOTOMY SORT: eight real situations, paired so each event
// sits next to the part of it that's actually yours to work with.
// ---------------------------------------------------------------------------
export const DICHOTOMY_ITEMS = [
  { id: "d1", text: "A test grade you already turned in.", correct: "OUTSIDE" },
  { id: "d2", text: "How much you study before the next one.", correct: "WITHIN" },
  { id: "d3", text: "A stranger's rude comment online.", correct: "OUTSIDE" },
  { id: "d4", text: "How long you let it live in your head afterward.", correct: "WITHIN" },
  { id: "d5", text: "Whether a college or job accepts your application.", correct: "OUTSIDE" },
  { id: "d6", text: "The honesty and effort you put into that application.", correct: "WITHIN" },
  { id: "d7", text: "A sudden rainstorm cancelling your plans.", correct: "OUTSIDE" },
  { id: "d8", text: "Your mood for the rest of the day because of it.", correct: "WITHIN" },
];

export const DICHOTOMY_OPTIONS = [
  { id: "WITHIN", label: "Within My Control" },
  { id: "OUTSIDE", label: "Outside My Control" },
];

// ---------------------------------------------------------------------------
// STAGE 2 — BUILD YOUR MORNING SYSTEM: pick exactly three. A system beats a
// pile of good intentions — a few things done daily outlast a long list
// done once.
// ---------------------------------------------------------------------------
export const MORNING_PRACTICES = [
  { id: "movement", label: "Movement", icon: "🏃", description: "A short walk or workout before anything else." },
  { id: "reflection", label: "Reflection", icon: "🧭", description: "Two quiet minutes thinking through the day ahead." },
  { id: "digital-silence", label: "Digital Silence", icon: "🔕", description: "No phone for the first 20 minutes awake." },
  { id: "preparation", label: "Preparation", icon: "📋", description: "Actually reviewing today's tasks instead of guessing at them." },
  { id: "gratitude", label: "Gratitude Note", icon: "📝", description: "Writing down one specific thing you're glad about." },
  { id: "cold-reset", label: "Cold Reset", icon: "💧", description: "A small physical reset — cold water, a stretch, a deep breath held on purpose." },
];

export const MORNING_SYSTEM_SIZE = 3;

// ---------------------------------------------------------------------------
// STAGE 3 — THE PAUSE: six provocations. Encounters 1–4 use a two-step
// mechanic — react immediately, or take the pause first — because the gap
// itself is the skill being tested, not just which words get chosen.
// Encounters 5–6 are sustained situations rather than sudden ones, so they
// skip straight to a direct choice.
// ---------------------------------------------------------------------------
export const PAUSE_ENCOUNTERS = [
  {
    id: "p1",
    hasPauseStep: true,
    label: "The Joke",
    text: "A friend makes a sharp joke at your expense in front of a group, and it stings more than they probably meant it to.",
    pausedChoices: [
      { id: "private-calm", label: "Bring it up with them privately, later, calmly", tag: "good", result: "They didn't even realize it landed wrong — and actually apologized once you said something." },
      { id: "silent-resent", label: "Say nothing out loud, but quietly hold onto it for weeks", tag: "weak", result: "Nothing got resolved. It just moved from the moment into a grudge." },
    ],
    reactNowResult: "You snapped back with something cutting. Everyone remembers the fight now, not the joke.",
  },
  {
    id: "p2",
    hasPauseStep: true,
    label: "The Unfair Grade",
    text: "A grade comes back way lower than you expected, and your first thought is that it isn't fair.",
    pausedChoices: [
      { id: "reread-ask", label: "Reread the feedback, then ask one specific, respectful question", tag: "good", result: "Turns out one section was genuinely miscounted — and asking calmly is exactly why it got fixed." },
      { id: "give-up-class", label: "Decide the class (and the teacher) are pointless and stop trying", tag: "weak", result: "The grade didn't get better. Neither did anything else in that class." },
    ],
    reactNowResult: "You fired off an angry message before rereading anything. It didn't help the grade, and it didn't feel great after, either.",
  },
  {
    id: "p3",
    hasPauseStep: true,
    label: "The Comment",
    text: "A genuinely rude comment shows up under something you posted.",
    pausedChoices: [
      { id: "consider-let-go", label: "Consider who's actually behind it, then let it go", tag: "good", result: "A stranger's bad day, projected onto you, stopped being your problem to carry." },
      { id: "reread-loop", label: "Reread it another dozen times over the next hour", tag: "weak", result: "One sentence from a stranger somehow ran the whole evening." },
    ],
    reactNowResult: "You clapped back publicly. The comment section got worse, and so did your afternoon.",
  },
  {
    id: "p4",
    hasPauseStep: true,
    label: "The Cancelled Plan",
    text: "Something you were genuinely excited about gets cancelled by something totally outside your control.",
    pausedChoices: [
      { id: "pivot", label: "Pick one thing that's still possible today, and do that instead", tag: "good", result: "Not the day you planned — still a decent one." },
      { id: "complain-all-day", label: "Spend the rest of the day telling everyone how ruined it is", tag: "weak", result: "The day stayed ruined, mostly because it got talked into staying that way." },
    ],
    reactNowResult: "You spiraled immediately — the whole day felt wrecked before it even had a chance not to be.",
  },
  {
    id: "p5",
    hasPauseStep: false,
    label: "Carrying Too Much",
    text: "This week is a lot — school, family stuff, a friend's drama all landing at once. It's genuinely heavy.",
    choices: [
      { id: "ask-help", label: "Tell a trusted adult or friend what's actually going on", tag: "good", result: "Saying it out loud made it lighter almost immediately — and they actually helped." },
      { id: "carry-alone", label: "Push through completely alone so no one thinks you can't handle it", tag: "bad", result: "You handled it, technically. It cost a lot more than it needed to." },
    ],
  },
  {
    id: "p6",
    hasPauseStep: false,
    label: "The Avoided Thing",
    text: "There's a hard conversation, or a hard task, you've been putting off for weeks. It gets heavier the longer it waits.",
    choices: [
      { id: "do-it", label: "Do the uncomfortable part today, in one sitting", tag: "good", result: "Fifteen genuinely uncomfortable minutes, and then it was just done." },
      { id: "wait-better-time", label: "Wait for a better time that keeps not arriving", tag: "bad", result: "Another week passed. The thing waiting at the end of it didn't get any smaller." },
    ],
  },
];
